import type { TwinRendererInfo } from './twin-renderer';

/**
 * The words of the renderer status (twin-renderer.ts), shared by the Twin's console and the IDE's Log
 * panel (BlocklyTestView.ts) so both say the same. No three import: the IDE bundle imports this file.
 */

/** `WebGPURenderer -> WebGPU (asked: webgpu, from url) | GPU: ... | notes | initialised after N ms`. */
export function twinRendererSummary(info: TwinRendererInfo): string {
  const gpu = info.adapter
    ? [info.adapter.vendor, info.adapter.architecture, info.adapter.description].filter(Boolean).join(' / ') || '(adapter gives no name)'
    : info.gl ? info.gl.renderer : '?';
  const notes = [
    info.fallback && 'WebGPU NOT available, fell back to WebGL2',
    info.software && 'SOFTWARE GPU',
    info.compatibilityMode && 'compatibility mode',
    !info.navigatorGpu && 'no navigator.gpu',
  ].filter(Boolean);
  return `${info.renderer ?? 'not created'} -> ${info.backend ?? '-'}`
    + ` (asked: ${info.requested}, from ${info.source}) | GPU: ${gpu}`
    + (notes.length ? ` | ${notes.join(', ')}` : '')
    + ` | initialised after ${info.initMs} ms`;
}

/** What the status says of its last event, in one line; '' for a value that is not a status. */
export function twinRendererStatusMessage(value: unknown): string {
  if (!isTwinRendererInfo(value)) return '';
  const info = value;
  switch (info.event) {
    case 'init':
      return twinRendererSummary(info) + (info.error ? ` | init FAILED: ${info.error}` : '');
    case 'first-render':
      return `first frame submitted after ${info.firstRenderSubmittedMs} ms (${info.renderer} -> ${info.backend})`;
    case 'gpu-work-done':
      return `GPU finished the work of the first frame after ${info.firstRenderGpuDoneMs} ms (WebGPU)`;
    case 'device-lost':
      return `${info.backend === 'WebGPU' ? 'device' : 'context'} LOST`
        + `${info.deviceLost?.reason ? ` (${info.deviceLost.reason})` : ''}: ${info.deviceLost?.message ?? ''}; the Twin no longer draws`;
    case 'gpu-error':
      return `${info.gpuErrors} GPU error(s), last: ${info.lastGpuError ?? '?'}`;
    default:
      return '';
  }
}

// Enough to format it: a status comes through postMessage from the Twin iframe.
function isTwinRendererInfo(value: unknown): value is TwinRendererInfo {
  const info = value as TwinRendererInfo | null;
  return !!info && typeof info === 'object' && typeof info.event === 'string' && typeof info.requested === 'string'
    && typeof info.source === 'string' && typeof info.gpuErrors === 'number';
}
